import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import { Box, Button, Menu, MenuItem, Popover, Tag, Text } from 'core/components';
import InputGroup from 'core/form/components/InputGroup';
import { dateTimeFormatter } from 'core/util/dateUtils';

import { getInvestigationStatusConfig } from 'app/stores/ai/investigationStatus';

const FILTER_THRESHOLD = 5;

// Component to render investigation sessions in a popover
@observer
class SessionsPopover extends Component {
  state = {
    isOpen: false,
    filterText: ''
  };

  handleInteraction = (nextOpenState) => {
    const { onOpenChange } = this.props;
    onOpenChange?.(nextOpenState);
    this.setState((prev) => ({ isOpen: nextOpenState, filterText: nextOpenState ? prev.filterText : '' }));
  };

  handleClose = () => {
    const { onOpenChange } = this.props;
    onOpenChange?.(false);
    this.setState({ isOpen: false, filterText: '' });
  };

  handleSelectSession = (sessionId) => {
    const { onSelectSession } = this.props;
    onSelectSession(sessionId);
    this.handleClose();
  };

  handleFilterChange = (e) => {
    this.setState({ filterText: e.target.value });
  };

  getOptions = () => {
    const { sessionsData } = this.props;
    const { filterText } = this.state;

    return sessionsData
      .map((session) => {
        const updatedAt = session.session?.get('updated_at');
        const statusConfig = getInvestigationStatusConfig(session.investigation?.status);

        return {
          value: session.session_id,
          title: session.title,
          updatedAt,
          statusConfig
        };
      })
      .filter(({ title }) => !filterText || title?.toLowerCase().includes(filterText.toLowerCase()));
  };

  get content() {
    const { sessionsData } = this.props;
    const { filterText } = this.state;
    const showFilter = sessionsData.length > FILTER_THRESHOLD;
    const options = this.getOptions();

    return (
      <Box>
        {showFilter && (
          <Box px={1} pt={1}>
            <InputGroup
              autoFocus
              small
              fill
              placeholder="Search sessions..."
              value={filterText}
              onChange={this.handleFilterChange}
            />
          </Box>
        )}
        <Menu style={{ width: 450, maxHeight: 400, overflow: 'auto' }}>
          {options.length === 0 && <MenuItem disabled text="No sessions found" small />}
          {options.map(({ value, title, updatedAt, statusConfig }) => (
            <div
              key={value}
              className={classNames(Classes.MENU_ITEM, Classes.SMALL)}
              onClick={() => this.handleSelectSession(value)}
              role="option"
              aria-selected={false}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && this.handleSelectSession(value)}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box display="flex" flexDirection="column" overflow="hidden" flex="1" minWidth={0} mr={2}>
                  <Text as="div" ellipsis mb={1}>
                    {title}
                  </Text>
                  {updatedAt && (
                    <Text as="div" fontSize="11px" color="muted">
                      {dateTimeFormatter({ value: updatedAt })}
                    </Text>
                  )}
                </Box>
                <Box flexShrink={0}>
                  <Tag intent={statusConfig.intent} small>
                    {statusConfig.label}
                  </Tag>
                </Box>
              </Box>
            </div>
          ))}
        </Menu>
      </Box>
    );
  }

  render() {
    const { sessionsData } = this.props;
    const { isOpen } = this.state;

    return (
      <Popover
        isOpen={isOpen}
        onInteraction={this.handleInteraction}
        content={this.content}
        boundary="viewport"
        position="bottom-left"
        targetTagName="span"
        canEscapeKeyClose
      >
        <Button
          small
          rightIcon="chevron-down"
          text={`${sessionsData.length} session${sessionsData.length !== 1 ? 's' : ''}`}
        />
      </Popover>
    );
  }
}

export default SessionsPopover;
